import type { TFunction } from "i18next";
import {
  ContractStatusDto,
  NeedWaitingListDto,
  type ContractReportQueryDto,
  type PersonProcurementWorkloadDto,
  type ProcurementWorkloadDto,
} from "../../autogen/bff-api";
import { base64Encode } from "../../common/utils/base64";
import { urls } from "../../urls";
import { contractScopeText } from "./contractFigures";
import { procurementScopeText } from "./procurementFigures";

export type WorkloadColumn = "needs" | "activeProcurements" | "activeContracts" | "deadlines";

export type WorkloadSortKey = "name" | WorkloadColumn;

export type WorkloadSort = { key: WorkloadSortKey; direction: "ascending" | "descending" } | null;

const SORT_KEYS: WorkloadSortKey[] = ["name", "needs", "activeProcurements", "activeContracts", "deadlines"];

export const workloadColumns = ({ scope }: ProcurementWorkloadDto): WorkloadColumn[] => [
  ...(scope.needs ? (["needs"] as const) : []),
  "activeProcurements",
  ...(scope.contracts ? (["activeContracts"] as const) : []),
  "deadlines",
];

export const columnLabel = (t: TFunction, column: WorkloadColumn): string =>
  ({
    needs: t("Needs"),
    activeProcurements: t("Active procurements"),
    activeContracts: t("Active contracts"),
    deadlines: t("Deadlines next 30 days"),
  })[column];

export const countOf = (person: PersonProcurementWorkloadDto, column: WorkloadColumn): number =>
  person[column] ?? 0;

export const parseWorkloadSort = (key: string | null, direction: string | null): WorkloadSort => {
  const known = SORT_KEYS.find((candidate) => candidate === key);
  if (!known) return null;
  return { key: known, direction: direction === "ascending" ? "ascending" : "descending" };
};

export const nextSort = (current: WorkloadSort, key: WorkloadSortKey): WorkloadSort => {
  if (current?.key !== key) return { key, direction: key === "name" ? "ascending" : "descending" };
  return { key, direction: current.direction === "ascending" ? "descending" : "ascending" };
};

export const sortedPeople = (
  people: PersonProcurementWorkloadDto[],
  sort: WorkloadSort,
  locale: string,
): PersonProcurementWorkloadDto[] => {
  if (!sort) return people;
  const sign = sort.direction === "ascending" ? 1 : -1;
  const byName = (a: PersonProcurementWorkloadDto, b: PersonProcurementWorkloadDto) =>
    a.name.localeCompare(b.name, locale);
  return [...people].sort((a, b) =>
    sort.key === "name"
      ? sign * byName(a, b)
      : sign * (countOf(a, sort.key) - countOf(b, sort.key)) || byName(a, b),
  );
};

export const workloadLink = (
  person: PersonProcurementWorkloadDto,
  column: Exclude<WorkloadColumn, "deadlines">,
  organizationId: string,
): string => {
  switch (column) {
    case "needs":
      return `${urls.app.needs.list}?${new URLSearchParams({
        waiting: NeedWaitingListDto.Mine,
        responsible: person.personId,
        responsibleName: person.name,
      })}`;
    case "activeProcurements":
      return `${urls.app.sourcingEvents.viewAll}?${new URLSearchParams({ responsible: person.personId, active: "true" })}`;
    case "activeContracts": {
      const query: ContractReportQueryDto = {
        owningOrganizationId: organizationId,
        statuses: [ContractStatusDto.Active],
        internalOwnerIds: [person.personId],
        limit: 10,
        skip: 0,
      };
      return `${urls.app.contracts.viewAll}?q=${base64Encode(JSON.stringify(query))}`;
    }
  }
};

export const workloadScopeText = (t: TFunction, workload: ProcurementWorkloadDto): string => {
  const { procurements, contracts, needs } = workload.scope;
  const procurementScope = procurementScopeText(t, procurements);
  const contractScope = contracts && contractScopeText(t, contracts);
  const needScope =
    needs && (needs.organizationWide ? String(t("the whole organisation")) : needs.departmentNames.join(", "));
  const parts = [
    { label: t("Procurements"), scope: procurementScope },
    ...(needScope ? [{ label: t("Needs"), scope: needScope }] : []),
    ...(contractScope ? [{ label: t("Contracts"), scope: contractScope }] : []),
  ];
  return parts.every((part) => part.scope === parts[0].scope)
    ? parts[0].scope
    : parts.map((part) => `${part.label}: ${part.scope}`).join(" · ");
};
